import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { EmptyState } from "@/components/EmptyState";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Search, Send, Loader2, Bot, Mic, Square, User2, Sparkles, MessageSquareText, Zap,
} from "lucide-react";

const initials = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
const time = (iso) => new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

export default function Inbox() {
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState("");
  const [text, setText] = useState("");
  const [mode, setMode] = useState("cliente"); // cliente = simular / atendente = responder
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [waStatus, setWaStatus] = useState(null);
  const scrollRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const activeRef = useRef(null);
  const busyRef = useRef(false);
  const navigate = useNavigate();

  const loadConvs = async (q = "") => {
    const { data } = await api.get("/conversations", { params: { search: q } });
    setConvs(data);
    const wanted = new URLSearchParams(window.location.search).get("conv");
    const target = wanted ? data.find((c) => c.id === wanted) : null;
    if (!activeRef.current && data.length && !q) selectConv(target || data[0]);
  };
  useEffect(() => {
    loadConvs();
    api.get("/company/whatsapp/status").then(({ data }) => setWaStatus(data)).catch(() => {});
  }, []); // eslint-disable-line

  // Tempo real: sincroniza lista e conversa ativa a cada 4s
  useEffect(() => {
    const t = setInterval(async () => {
      if (busyRef.current) return;
      try {
        const { data } = await api.get("/conversations", { params: { search } });
        setConvs(data);
        if (activeRef.current) {
          const { data: m } = await api.get(`/conversations/${activeRef.current.id}/messages`);
          setMessages((prev) => (m.messages.length !== prev.length ? m.messages : prev));
        }
      } catch { /* ignore */ }
    }, 4000);
    return () => clearInterval(t);
  }, [search]);

  const selectConv = async (c) => {
    setActive(c); activeRef.current = c;
    const { data } = await api.get(`/conversations/${c.id}/messages`);
    setMessages(data.messages);
    setConvs((prev) => prev.map((x) => (x.id === c.id ? { ...x, unread: 0 } : x)));
    setTimeout(() => scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }), 60);
  };

  useEffect(() => { scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim() || !active || sending) return;
    setSending(true); busyRef.current = true;
    const body = text.trim();
    setText("");
    try {
      if (mode === "cliente") {
        const { data } = await api.post(`/conversations/${active.id}/simulate`, { text: body });
        setMessages((m) => [...m, data.incoming, ...(data.reply ? [data.reply] : [])]);
      } else {
        const { data } = await api.post(`/conversations/${active.id}/send`, { text: body });
        setMessages((m) => [...m, data.message]);
      }
    } catch { toast.error("Erro ao enviar mensagem."); }
    finally { setSending(false); busyRef.current = false; }
  };

  const toggleAi = async () => {
    if (!active) return;
    try {
      const { data } = await api.post(`/conversations/${active.id}/ai-toggle`);
      const upd = { ...active, ai_paused: data.ai_paused };
      setActive(upd); activeRef.current = upd;
      setConvs((prev) => prev.map((x) => (x.id === active.id ? { ...x, ai_paused: data.ai_paused } : x)));
      toast[data.ai_paused ? "info" : "success"](data.ai_paused ? "IA pausada nesta conversa. Você responde manualmente." : "IA voltou a responder nesta conversa.");
      if (data.ai_paused) setMode("atendente");
    } catch { toast.error("Não foi possível alterar a IA."); }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRef.current = mr; chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await sendVoice(blob);
      };
      mr.start();
      setRecording(true);
    } catch { toast.error("Não foi possível acessar o microfone."); }
  };
  const stopRec = () => { mediaRef.current?.stop(); setRecording(false); };

  const sendVoice = async (blob) => {
    if (!active) return;
    setTranscribing(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "voz.webm");
      const { data } = await api.post(`/conversations/${active.id}/voice`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMessages((m) => [...m, data.incoming, ...(data.reply ? [data.reply] : [])]);
      toast.success("Áudio transcrito pelo Whisper!");
    } catch { toast.error("Falha ao transcrever o áudio."); }
    finally { setTranscribing(false); }
  };

  return (
    <div className="mx-auto max-w-7xl" data-testid="inbox-page">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="mb-1 inline-flex items-center gap-2 text-xs font-mono-flow uppercase tracking-widest text-cyan-400"><Sparkles className="h-3.5 w-3.5" /> Tempo real</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">Inbox WhatsApp</h1>
        </div>
        {waStatus && (
          <div className={`flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm ${waStatus.status === "conectado" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-amber-500/30 bg-amber-500/10 text-amber-300"}`} data-testid="inbox-wa-status">
            <span className={`h-2 w-2 rounded-full ${waStatus.status === "conectado" ? "bg-emerald-400 pulse-ring" : "bg-amber-400"}`} />
            {waStatus.status === "conectado" ? `WhatsApp ao vivo${waStatus.number ? ` · +${waStatus.number}` : ""}` : "WhatsApp desconectado — conecte em Configurações"}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[340px_1fr]">
        {/* Conversations */}
        <div className="glow-card flex h-[72vh] flex-col rounded-2xl">
          <div className="border-b border-slate-800 p-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input data-testid="inbox-search-input" value={search} onChange={(e) => { setSearch(e.target.value); loadConvs(e.target.value); }}
                placeholder="Buscar conversa..." className="h-10 border-slate-700 bg-slate-900/60 pl-10 text-slate-100" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {convs.map((c) => (
              <button key={c.id} onClick={() => selectConv(c)} data-testid="inbox-chat-item"
                className={`flex w-full items-center gap-3 border-b border-slate-800/50 px-3 py-3 text-left transition-colors ${active?.id === c.id ? "bg-cyan-400/10" : "hover:bg-slate-900/60"}`}>
                <Avatar className="h-10 w-10"><AvatarFallback className="bg-slate-800 text-sm text-cyan-300">{initials(c.client_name)}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate text-sm font-semibold text-slate-100">{c.client_name}</p>
                    <span className="shrink-0 text-[11px] text-slate-500">{time(c.last_at)}</span>
                  </div>
                  <p className="truncate text-xs text-slate-400">{c.last_message}</p>
                </div>
                {c.unread > 0 && <span className="grid h-5 min-w-5 place-items-center rounded-full bg-cyan-400 px-1 text-[11px] font-bold text-slate-950">{c.unread}</span>}
              </button>
            ))}
            {convs.length === 0 && (
              <EmptyState compact icon={MessageSquareText} testid="inbox-empty"
                title={search ? "Nenhuma conversa encontrada" : "Nenhuma conversa ainda"}
                desc={search ? "Tente outro nome de cliente." : "Quando um cliente escrever no WhatsApp, a conversa aparece aqui e a IA responde na hora."}
                actionLabel={search ? undefined : "Simular conversa de cliente"}
                onAction={() => navigate("/dashboard", { state: { runDemo: true } })} />
            )}
          </div>
        </div>

        {/* Chat window */}
        <div className="glow-card flex h-[72vh] flex-col rounded-2xl">
          {!active ? (
            <div className="grid flex-1 place-items-center">
              <EmptyState icon={Bot} testid="inbox-no-active" title="Sua assistente está pronta"
                desc="Selecione uma conversa ao lado para acompanhar a IA respondendo seus clientes em tempo real." />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
                <div className="flex items-center gap-3">
                  <Avatar className="h-9 w-9"><AvatarFallback className="bg-slate-800 text-sm text-cyan-300">{initials(active.client_name)}</AvatarFallback></Avatar>
                  <div>
                    <p className="text-sm font-semibold text-slate-100">{active.client_name}</p>
                    <p className="text-xs text-emerald-400">online</p>
                  </div>
                </div>
                <button onClick={toggleAi} data-testid="inbox-ai-toggle-switch"
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-all ${active.ai_paused ? "border-amber-500/30 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20" : "border-cyan-400/30 bg-cyan-400/10 text-cyan-300 hover:bg-cyan-400/20"}`}
                  title={active.ai_paused ? "Clique para a IA voltar a responder" : "Clique para pausar a IA e responder você mesmo"}>
                  <Bot className="h-3.5 w-3.5" /> {active.ai_paused ? "IA pausada · você no comando" : "IA respondendo"}
                </button>
              </div>

              <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`} data-testid="inbox-message">
                    <div className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 text-sm ${m.direction === "out" ? (m.ai_generated ? "bg-cyan-400/15 text-cyan-50 border border-cyan-400/30" : "bg-emerald-500/15 text-emerald-50 border border-emerald-500/25") : "bg-slate-800/80 text-slate-100"}`}>
                      {m.type === "voice" && (
                        <div className="mb-1.5 flex items-center gap-2 rounded-lg bg-slate-950/40 px-2 py-1.5 text-xs text-slate-300" data-testid="inbox-whisper-audio-player">
                          <Mic className="h-3.5 w-3.5 text-cyan-400" /> Mensagem de voz · transcrito por Whisper
                        </div>
                      )}
                      {m.ai_generated && <div className="mb-1 flex items-center gap-1 text-[11px] font-medium text-cyan-400"><Zap className="h-3 w-3" /> IA{m.action_result?.ok && <span className="ml-1 rounded-full bg-emerald-500/20 px-1.5 text-emerald-300" data-testid="inbox-ai-action-badge">agenda atualizada</span>}</div>}
                      <p className="whitespace-pre-wrap leading-relaxed">{m.content}</p>
                      <p className="mt-1 text-right text-[10px] opacity-60">{time(m.created_at)}</p>
                    </div>
                  </div>
                ))}
                {(sending || transcribing) && (
                  <div className="flex justify-start"><div className="flex items-center gap-2 rounded-2xl bg-slate-800/80 px-3.5 py-2.5 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin text-cyan-400" /> {transcribing ? "Transcrevendo áudio..." : "IA digitando..."}</div></div>
                )}
              </div>

              {/* Composer */}
              <div className="border-t border-slate-800 p-3">
                <div className="mb-2 flex items-center gap-1 rounded-lg bg-slate-900/70 p-1 text-xs">
                  <button onClick={() => setMode("cliente")} className={`flex items-center gap-1 rounded-md px-2.5 py-1 font-medium transition-all ${mode === "cliente" ? "bg-cyan-400 text-slate-950" : "text-slate-400"}`} data-testid="inbox-mode-client">
                    <User2 className="h-3.5 w-3.5" /> Simular cliente (IA responde)
                  </button>
                  <button onClick={() => setMode("atendente")} className={`flex items-center gap-1 rounded-md px-2.5 py-1 font-medium transition-all ${mode === "atendente" ? "bg-emerald-500 text-slate-950" : "text-slate-400"}`} data-testid="inbox-mode-agent">
                    Responder manual
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={recording ? stopRec : startRec} disabled={transcribing}
                    className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl border transition-all ${recording ? "border-red-500 bg-red-500/20 text-red-400 pulse-ring" : "border-slate-700 bg-slate-900/60 text-slate-300 hover:text-cyan-300"}`}
                    data-testid="inbox-voice-btn" title="Enviar mensagem de voz (Whisper)">
                    {recording ? <Square className="h-4 w-4" /> : <Mic className="h-5 w-5" />}
                  </button>
                  <Input data-testid="inbox-message-input" value={text} onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && send()}
                    placeholder={mode === "cliente" ? "Escreva como o cliente..." : "Responda ao cliente..."}
                    className="h-11 border-slate-700 bg-slate-900/60 text-slate-100" />
                  <Button onClick={send} disabled={sending || !text.trim()} data-testid="inbox-send-button"
                    className="h-11 bg-cyan-400 px-4 font-semibold text-slate-950 hover:bg-cyan-300">
                    {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
